import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button } from './ui';
import { api } from '../lib/api';
import SpendingSegmentsPanel from './SpendingSegmentsPanel';
import { generateTradeAreaReportPDF } from '../utils/pdfBuilder';
import { exportFileName } from '../utils/pdfBranding';
import { fileExportedPdf, describeOutcome, type ExportOutcome } from '../utils/exportFiling';
import { haversineMeters } from './map/geo';

interface Ring {
  miles: number;
  population: number;
  households: number;
  housing_units: number;
  owner_occupied: number;
  renter_occupied: number;
  employed_pop: number;
  bachelors_plus: number;
  median_household_income: number | null;
  median_age: number | null;
  block_groups: number;
}

interface Props {
  lat: number;
  lng: number;
  /** Controlled radii (map usage). When omitted, the panel manages radii internally. */
  radii?: number[];
  onRadiiChange?: (radii: number[]) => void;
  /** Subject/target of the trade area, used in the exported report. */
  subject?: { label: string; lat: number; lng: number; isProperty: boolean };
  /** The Property Database record this panel sits on - the report files against it. Absent on the map. */
  masterPropertyId?: string;
}

const fmt = (n: number | null | undefined) =>
  n == null ? '-' : n.toLocaleString('en-US', { maximumFractionDigits: 0 });
const money = (n: number | null | undefined) =>
  n == null ? '-' : '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
const pct = (part: number, whole: number) =>
  whole > 0 ? Math.round((part / whole) * 100) + '%' : '-';

// Clamp/dedupe/sort a radii list to at most 3 valid values (0.25–50 mi).
function normalizeRadii(input: number[]): number[] {
  const clean = Array.from(new Set(input.filter((r) => Number.isFinite(r) && r >= 0.25 && r <= 50)))
    .sort((a, b) => a - b)
    .slice(0, 3);
  return clean.length ? clean : [1, 3, 5];
}

// Building permits within the trade area -> a Development Activity summary for the report.
async function buildPermits(lat: number, lng: number, maxMiles: number) {
  const res = await api.getMapPermits();
  const all = (res.permits || []) as any[];
  const meters = maxMiles * 1609.34;
  const within = all.filter(
    (p) => p.latitude != null && p.longitude != null &&
      haversineMeters([lat, lng], [Number(p.latitude), Number(p.longitude)]) <= meters,
  );
  if (!within.length) return null;
  const totalValuation = within.reduce((s, p) => s + (Number(p.valuation) || 0), 0);
  const recent = [...within]
    .filter((p) => p.issue_date)
    .sort((a, b) => String(b.issue_date).localeCompare(String(a.issue_date)))
    .slice(0, 3)
    .map((p) => ({
      desc: String(p.description || p.permit_type || p.property_address || 'Permit').slice(0, 60),
      date: p.issue_date ? String(p.issue_date).slice(0, 10) : '',
      valuation: p.valuation != null ? Number(p.valuation) : null,
    }));
  return { count: within.length, totalValuation, recent };
}

// Nearby recent sales within the trade area -> a small comps table for the report.
async function buildComps(lat: number, lng: number, maxMiles: number) {
  const near = await api.findNearbyProperties({ latitude: String(lat), longitude: String(lng), radius_miles: String(maxMiles) });
  const ids = ((near.properties || []) as any[]).map((p) => p.id).filter(Boolean).slice(0, 200);
  if (!ids.length) return null;
  const cd = await api.getCompData(ids);
  const comps = ((cd.comps || []) as any[])
    .filter((c) => c.sale_price != null && Number(c.sale_price) > 0)
    .sort((a, b) => String(b.sale_date || '').localeCompare(String(a.sale_date || '')))
    .slice(0, 5)
    .map((c) => ({
      address: c.address || '—',
      city: c.city || undefined,
      sale_price: c.sale_price != null ? Number(c.sale_price) : null,
      price_per_sf: c.price_per_sf != null ? Number(c.price_per_sf) : null,
      sale_date: c.sale_date ? String(c.sale_date).slice(0, 10) : undefined,
    }));
  return comps.length ? comps : null;
}

export default function DemographicsPanel({ lat, lng, radii, onRadiiChange, subject, masterPropertyId }: Props) {
  const controlled = radii !== undefined;
  const [internalRadii, setInternalRadii] = useState<number[]>([1, 3, 5]);
  const activeRadii = controlled ? (radii as number[]) : internalRadii;
  const radiiKey = activeRadii.join(',');

  const [rings, setRings] = useState<Ring[]>([]);
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Radius editor: draft inputs (1–3) that commit on "Apply".
  const [draft, setDraft] = useState<string[]>(activeRadii.map(String));
  useEffect(() => { setDraft(activeRadii.map(String)); }, [radiiKey]);

  const setRadii = (next: number[]) => {
    const clean = normalizeRadii(next);
    if (controlled) onRadiiChange?.(clean);
    else setInternalRadii(clean);
  };

  const load = useCallback(async (rad: number[]) => {
    setLoading(true); setError(null);
    try {
      const res = await api.getDemographics(lat, lng, rad);
      setRings(res.rings || []);
      setSource(res.source || '');
    } catch (e: any) {
      setError(e?.message || 'Failed to load demographics');
    } finally {
      setLoading(false);
    }
  }, [lat, lng]);

  useEffect(() => { load(activeRadii); }, [load, radiiKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Trade Area Report (PDF) ──
  const [lastSpending, setLastSpending] = useState<any>(null);
  const [includeSpending, setIncludeSpending] = useState(false);
  const [exporting, setExporting] = useState(false);
  /** Where the last report went - the property record, or why it was not filed. */
  const [exportNote, setExportNote] = useState<string | null>(null);

  const exportReport = async () => {
    if (!rings.length) return;
    setExporting(true);
    try {
      const maxMiles = Math.max(...activeRadii);

      // All sub-fetches are best-effort — a failure just omits that section.
      const [mapImageBase64, spending, permits, comps] = await Promise.all([
        api.getStaticMap(lat, lng, activeRadii).then((r) => r.image as string).catch(() => undefined),
        (async () => {
          if (!includeSpending) return null;
          if (lastSpending) return lastSpending;
          return api.getSpending(lat, lng, activeRadii).catch(() => null);
        })(),
        buildPermits(lat, lng, maxMiles).catch(() => null),
        buildComps(lat, lng, maxMiles).catch(() => null),
      ]);

      const subj = subject ?? { label: 'Trade Area', lat, lng, isProperty: false };
      const doc = await generateTradeAreaReportPDF({
        subject: subj,
        rings,
        mapImageBase64,
        spending,
        permits,
        comps,
        companyName: 'Apex Real Estate Services',
      });
      // Saved FIRST, then filed against the Property Database record when there is one - the map's
      // "selected location" has no record to file against, and says so.
      const fileName = exportFileName('Trade Area Report', subj.label);
      doc.save(fileName);
      const outcome: ExportOutcome = masterPropertyId
        ? await fileExportedPdf(doc, { kind: 'Trade Area Report', fileName, masterPropertyId })
        : { fileName, filed: null, error: 'no property record to file against (a map location)' };
      setExportNote(describeOutcome(outcome));
    } finally {
      setExporting(false);
    }
  };

  const isPreset = radiiKey === '1,3,5';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle>Trade-Area Demographics</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setRadii([1, 3, 5])}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${isPreset ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:text-gray-800'}`}
            >1 / 3 / 5 mi</button>
            {/* Up to 3 custom radii */}
            <div className="flex items-center gap-1">
              {draft.map((v, i) => (
                <span key={i} className="inline-flex items-center">
                  <input
                    type="number" min="0.25" max="50" step="0.25" value={v}
                    onChange={(e) => setDraft((d) => d.map((x, j) => (j === i ? e.target.value : x)))}
                    className="w-16 border border-gray-300 rounded-lg px-2 py-1 text-sm"
                  />
                  {draft.length > 1 && (
                    <button onClick={() => setDraft((d) => d.filter((_, j) => j !== i))} className="ml-0.5 text-gray-400 hover:text-gray-600" aria-label="Remove radius">×</button>
                  )}
                </span>
              ))}
              {draft.length < 3 && (
                <button onClick={() => setDraft((d) => [...d, ''])} className="px-2 py-1 text-sm rounded border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50" aria-label="Add radius">+</button>
              )}
              <span className="text-xs text-gray-500">mi</span>
              <Button variant="outline" className="text-sm py-1" onClick={() => setRadii(draft.map(Number))}>Apply</Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-gray-500 text-sm text-center py-6">Loading demographics...</p>
        ) : error ? (
          <p className="text-red-600 text-sm text-center py-6">{error}</p>
        ) : rings.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-6">No demographic data for this location (coverage: Riverside + San Bernardino counties).</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2 pr-3">Metric</th>
                    {rings.map((r) => (
                      <th key={r.miles} className="py-2 px-3 text-right">{r.miles} mi</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Population', get: (r: Ring) => fmt(r.population) },
                    { label: 'Households', get: (r: Ring) => fmt(r.households) },
                    { label: 'Median HH Income', get: (r: Ring) => money(r.median_household_income) + ' est.' },
                    { label: 'Median Age', get: (r: Ring) => r.median_age == null ? '-' : String(r.median_age) },
                    { label: 'Housing Units', get: (r: Ring) => fmt(r.housing_units) },
                    { label: 'Owner-Occupied', get: (r: Ring) => `${fmt(r.owner_occupied)} (${pct(r.owner_occupied, r.owner_occupied + r.renter_occupied)})` },
                    { label: 'Renter-Occupied', get: (r: Ring) => `${fmt(r.renter_occupied)} (${pct(r.renter_occupied, r.owner_occupied + r.renter_occupied)})` },
                    { label: "Bachelor's+", get: (r: Ring) => fmt(r.bachelors_plus) },
                  ].map((row) => (
                    <tr key={row.label} className="border-b border-gray-50">
                      <td className="py-2 pr-3 text-gray-600">{row.label}</td>
                      {rings.map((r) => (
                        <td key={r.miles} className="py-2 px-3 text-right font-medium text-gray-900">{row.get(r)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Source: {source}. Medians are population-weighted estimates across block groups.
            </p>

            {/* Premium (paid, on-demand) consumer spending + Tapestry segmentation for the same rings. */}
            <SpendingSegmentsPanel lat={lat} lng={lng} radii={activeRadii} onLoaded={setLastSpending} />

            {/* Trade Area Report (PDF) */}
            <div className="mt-4 border-t border-gray-100 pt-3">
              <label className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                <input type="checkbox" checked={includeSpending} onChange={(e) => setIncludeSpending(e.target.checked)} />
                Include Spending &amp; Segments (Esri{lastSpending ? '' : ', ~$0.08'})
              </label>
              <Button variant="outline" className="w-full text-sm" onClick={exportReport} disabled={exporting}>
                {exporting ? 'Building report…' : '⬇ Export Trade Area Report (PDF)'}
              </Button>
              {exportNote && (
                <p className="mt-2 text-xs text-gray-600" data-testid="trade-area-export-note">{exportNote}</p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
